/* ==========================================================================
   MP — Collection  (sections/mp-collection.liquid)
   --------------------------------------------------------------------------
   Vanilla JS, no dependencies. Handles:
   1. Sort select      — submits the sort form (filters preserved via hidden
                         inputs) for a normal full-page navigation.
   2. Filter drawer    — open/close, Esc, backdrop click, focus management.
                         "Apply filters" is a plain GET form submit (native
                         Shopify storefront filtering), no AJAX needed.
   3. More products    — fetches the next page via the Section Rendering API,
                         appends the new cards, updates/hides the button.
                         Without JS the standard pagination links render
                         instead (the button ships with [hidden]).
   4. Colour selector  — swatch row in the toolbar. Picking a colour swaps
                         every card's image/link to that colour's variant
                         (per-card JSON embedded by the section) and dims
                         cards without that colour. Re-applied to cards
                         appended by "More products".
   Progressive enhancement + double-init guard (theme editor safe).
   ========================================================================== */

(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function initSection(root) {
    if (!root || root.dataset.mpCollectionInit === 'true') return;
    root.dataset.mpCollectionInit = 'true';

    var sectionId = root.dataset.sectionId;

    /* ------------------------------------------------------------ Sorting */
    var sortForm = root.querySelector('[data-mp-sort-form]');
    var sortSelect = root.querySelector('[data-mp-sort]');
    var sortLabel = root.querySelector('[data-mp-sort-label]');

    if (sortForm && sortSelect) {
      sortSelect.addEventListener('change', function () {
        if (sortLabel && sortSelect.selectedIndex > -1) {
          sortLabel.textContent = sortSelect.options[sortSelect.selectedIndex].text.trim();
        }
        if (typeof sortForm.requestSubmit === 'function') {
          sortForm.requestSubmit();
        } else {
          sortForm.submit();
        }
      });
    }

    /* ------------------------------------------------------ Filter drawer */
    var drawer = root.querySelector('[data-mp-drawer]');
    var drawerPanel = root.querySelector('[data-mp-drawer-panel]');
    var openButtons = root.querySelectorAll('[data-mp-drawer-open]');
    var lastFocused = null;

    function setExpanded(value) {
      openButtons.forEach(function (btn) {
        btn.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }
      if (event.key === 'Tab' && drawerPanel) {
        var focusables = drawerPanel.querySelectorAll(FOCUSABLE);
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    function openDrawer() {
      if (!drawer) return;
      lastFocused = document.activeElement;
      drawer.setAttribute('data-state', 'open');
      setExpanded(true);
      document.body.classList.add('mp-collection-drawer-open');
      document.addEventListener('keydown', onKeydown);
      var closeBtn = drawer.querySelector('[data-mp-drawer-close]');
      var target = closeBtn || (drawerPanel && drawerPanel.querySelector(FOCUSABLE));
      if (target) {
        window.requestAnimationFrame(function () {
          target.focus();
        });
      }
    }

    function closeDrawer() {
      if (!drawer || drawer.getAttribute('data-state') !== 'open') return;
      drawer.setAttribute('data-state', 'closed');
      setExpanded(false);
      document.body.classList.remove('mp-collection-drawer-open');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
      lastFocused = null;
    }

    if (drawer) {
      openButtons.forEach(function (btn) {
        btn.addEventListener('click', openDrawer);
      });

      drawer.querySelectorAll('[data-mp-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', closeDrawer);
      });

      // Backdrop click (the overlay itself, not the panel)
      drawer.addEventListener('click', function (event) {
        if (event.target === drawer) closeDrawer();
      });

      // Theme editor: if this section is unloaded while the drawer is open,
      // close it so the document-level keydown listener is detached.
      document.addEventListener('shopify:section:unload', function onUnload(event) {
        if (event.target && !event.target.contains(root)) return;
        closeDrawer();
        document.removeEventListener('shopify:section:unload', onUnload);
      });
    }

    /* --------------------------------------------------------- Load more */
    var grid = root.querySelector('[data-mp-grid]');
    var loadMoreBtn = root.querySelector('[data-mp-load-more]');
    var pagination = root.querySelector('[data-mp-pagination]');
    var loadMoreLabel = root.querySelector('[data-mp-load-more-label]');

    if (grid && loadMoreBtn) {
      // JS available: swap the pagination links for the load-more button.
      loadMoreBtn.hidden = false;
      if (pagination) pagination.hidden = true;
      loadMoreBtn.addEventListener('click', loadMore);
    }

    function loadMore() {
      var nextUrl = loadMoreBtn.getAttribute('data-next-url');
      if (!nextUrl || loadMoreBtn.disabled) return;

      loadMoreBtn.disabled = true;
      if (loadMoreLabel) {
        loadMoreLabel.textContent = loadMoreBtn.getAttribute('data-loading-label') || 'Loading…';
      }

      var separator = nextUrl.indexOf('?') > -1 ? '&' : '?';
      var fetchUrl = nextUrl + separator + 'section_id=' + encodeURIComponent(sectionId);

      fetch(fetchUrl)
        .then(function (response) {
          if (!response.ok) throw new Error('MP Collection: load more failed (' + response.status + ')');
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          doc.querySelectorAll('[data-mp-card]').forEach(function (card) {
            var node = document.importNode(card, true);
            grid.appendChild(node);
            // Newly appended cards respect the active colour selection.
            if (activeColor) applyColorToCard(node, activeColor);
          });

          var nextBtn = doc.querySelector('[data-mp-load-more]');
          var newNextUrl = nextBtn && nextBtn.getAttribute('data-next-url');

          if (newNextUrl) {
            loadMoreBtn.setAttribute('data-next-url', newNextUrl);
            loadMoreBtn.disabled = false;
            if (loadMoreLabel) {
              loadMoreLabel.textContent = loadMoreBtn.getAttribute('data-label') || 'More products';
            }
          } else {
            loadMoreBtn.hidden = true;
          }
        })
        .catch(function () {
          // Graceful fallback: navigate to the next page normally.
          window.location.href = nextUrl;
        });
    }

    /* ------------------------------------------------- Sticky toolbar fade */
    // A zero-height sentinel right before the sticky wrap: when it scrolls
    // out of view the toolbar is stuck, and .is-stuck fades the box to the
    // page background with a subtle bottom stroke (see mp-collection.css).
    var toolbarWrap = root.querySelector('.mp-collection__toolbar-wrap');
    var prevSibling = toolbarWrap ? toolbarWrap.previousElementSibling : null;
    var hasSentinel = prevSibling && prevSibling.hasAttribute('data-mp-sticky-sentinel');
    if (toolbarWrap && 'IntersectionObserver' in window && !hasSentinel) {
      var sentinel = document.createElement('div');
      sentinel.setAttribute('data-mp-sticky-sentinel', '');
      toolbarWrap.parentNode.insertBefore(sentinel, toolbarWrap);
      new IntersectionObserver(function (entries) {
        toolbarWrap.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }).observe(sentinel);
    }

    // The theme's sticky header reveals on scroll-up (translateY animation,
    // z-index 12). Track its visible bottom edge per animation frame and use
    // it as the toolbar's sticky offset, so the toolbar rides just below the
    // header instead of being covered by it.
    var headerSection = document.querySelector('.shopify-section-header');
    if (toolbarWrap && headerSection) {
      var syncFramesLeft = 0;
      var syncToolbarTop = function () {
        var top = 0;
        var position = window.getComputedStyle(headerSection).position;
        if (position === 'sticky' || position === 'fixed') {
          top = Math.max(0, Math.round(headerSection.getBoundingClientRect().bottom));
        }
        toolbarWrap.style.top = top + 'px';
        if (syncFramesLeft > 0) {
          syncFramesLeft--;
          window.requestAnimationFrame(syncToolbarTop);
        }
      };
      var queueToolbarSync = function () {
        // ~20 frames covers the header's 150ms reveal/hide transition.
        var wasIdle = syncFramesLeft <= 0;
        syncFramesLeft = 20;
        if (wasIdle) window.requestAnimationFrame(syncToolbarTop);
      };
      window.addEventListener('scroll', queueToolbarSync, { passive: true });
      headerSection.addEventListener('transitionend', queueToolbarSync);
      queueToolbarSync();
    }

    /* ----------------------------------------------------- Colour selector */
    var colorBar = root.querySelector('[data-mp-color-bar]');
    var activeColor = null;
    var COLOR_STORE_KEY = 'mp-collection-colour';

    // Per-card colour map: { "<handle>": { id, img, img2x, w, h }, … }
    // embedded by the section as <script type="application/json">.
    function cardColorData(card) {
      if (card.mpColorData !== undefined) return card.mpColorData;
      var el = card.querySelector('[data-mp-color-data]');
      var data = null;
      if (el) {
        try {
          data = JSON.parse(el.textContent);
        } catch (error) {
          data = null;
        }
      }
      card.mpColorData = data;
      return data;
    }

    // Snapshot the card's original image + link hrefs before the first swap.
    function ensureOriginal(card) {
      if (card.mpColorOriginal) return;
      var img = card.querySelector('.mp-collection__card-media img');
      var links = [];
      card.querySelectorAll('a[href]').forEach(function (link) {
        links.push({ el: link, href: link.getAttribute('href') });
      });
      card.mpColorOriginal = {
        img: img
          ? {
              src: img.getAttribute('src'),
              srcset: img.getAttribute('srcset'),
              width: img.getAttribute('width'),
              height: img.getAttribute('height')
            }
          : null,
        links: links
      };
    }

    function restoreCardImage(card) {
      var original = card.mpColorOriginal;
      if (!original || !original.img) return;
      var img = card.querySelector('.mp-collection__card-media img');
      if (!img) return;
      img.setAttribute('src', original.img.src);
      if (original.img.srcset) img.setAttribute('srcset', original.img.srcset);
      if (original.img.width) img.setAttribute('width', original.img.width);
      if (original.img.height) img.setAttribute('height', original.img.height);
    }

    function restoreCard(card) {
      var original = card.mpColorOriginal;
      if (!original) return;
      restoreCardImage(card);
      original.links.forEach(function (link) {
        link.el.setAttribute('href', link.href);
      });
    }

    function applyColorToCard(card, handle) {
      if (!handle) {
        restoreCard(card);
        card.classList.remove('mp-colour-mute');
        return;
      }

      var data = cardColorData(card);
      var entry = data && data[handle];

      if (!entry) {
        // Product has no variant in this colour: reset any earlier swap and
        // de-emphasize the card (it keeps its place — no relayout) — unless
        // it's exempted (section setting "Excluded from colour dimming").
        restoreCard(card);
        if (!card.hasAttribute('data-mp-color-exempt')) {
          card.classList.add('mp-colour-mute');
        }
        return;
      }

      ensureOriginal(card);
      card.classList.remove('mp-colour-mute');

      var img = card.querySelector('.mp-collection__card-media img');
      if (img) {
        if (entry.img) {
          img.setAttribute('src', entry.img);
          var srcset = entry.img + ' 600w';
          if (entry.img2x) srcset += ', ' + entry.img2x + ' 1200w';
          img.setAttribute('srcset', srcset);
          if (entry.w) img.setAttribute('width', entry.w);
          if (entry.h) img.setAttribute('height', entry.h);
        } else {
          // Variant exists but has no image: keep the original image.
          restoreCardImage(card);
        }
      }

      card.mpColorOriginal.links.forEach(function (link) {
        var separator = link.href.indexOf('?') > -1 ? '&' : '?';
        link.el.setAttribute('href', link.href + separator + 'variant=' + entry.id);
      });
    }

    if (colorBar && grid) {
      var swatchButtons = colorBar.querySelectorAll('[data-mp-color]');
      var clearButton = colorBar.querySelector('[data-mp-color-clear]');

      var setActiveColor = function (handle) {
        activeColor = handle || null;

        swatchButtons.forEach(function (btn) {
          var isActive = btn.getAttribute('data-mp-color') === activeColor;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        if (clearButton) {
          // "Clear" only exists while a colour is selected.
          clearButton.hidden = !activeColor;
        }

        grid.querySelectorAll('[data-mp-card]').forEach(function (card) {
          applyColorToCard(card, activeColor);
        });

        try {
          if (activeColor) {
            window.sessionStorage.setItem(COLOR_STORE_KEY, activeColor);
          } else {
            window.sessionStorage.removeItem(COLOR_STORE_KEY);
          }
        } catch (error) {
          /* sessionStorage unavailable — selection just won't persist */
        }
      };

      swatchButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var handle = btn.getAttribute('data-mp-color');
          // Clicking the active swatch again clears the selection.
          setActiveColor(handle === activeColor ? null : handle);
        });
      });

      if (clearButton) {
        clearButton.addEventListener('click', function () {
          setActiveColor(null);
        });
      }

      // Nice-to-have: restore the last selection within this tab session,
      // but only if that colour is actually available in this grid.
      try {
        var stored = window.sessionStorage.getItem(COLOR_STORE_KEY);
        if (stored && colorBar.querySelector('[data-mp-color="' + stored + '"]')) {
          setActiveColor(stored);
        }
      } catch (error) {
        /* noop */
      }
    }
  }

  function initAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    if (context !== document && context.matches && context.matches('[data-mp-collection]')) {
      initSection(context);
    }
    context.querySelectorAll('[data-mp-collection]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is re-rendered, and make sure a
  // stale scroll lock never survives a section unload.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function () {
    document.body.classList.remove('mp-collection-drawer-open');
  });
})();
